import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ABOUT_IMAGE } from "@/data/works";

const Counter = ({ to, suffix = "", duration = 1800 }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * to));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
};

const STATS = [
  { to: 500, suffix: "+", key: "about.stats.projects" },
  { to: 100, suffix: "%", key: "about.stats.clients" },
  { to: 20, suffix: "+", key: "about.stats.experience" },
  { to: 100, suffix: "%", key: "about.stats.custom" },
];

const About = () => {
  const { t, lang } = useLang();
  const site = useSite();
  const o = (site.about && site.about[lang]) || {};
  const title = o.title || t("about.title");
  const founded = o.founded || t("about.founded");
  const p1 = o.p1 || t("about.p1");
  const p2 = o.p2 || t("about.p2");

  return (
    <section id="about" className="relative py-16 md:py-36 px-6 md:px-12 bg-background" style={{ scrollMarginTop: 80 }}>
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -top-5 -left-5 w-28 h-28 border-t border-l border-gold/40 z-10" />
            <div className="absolute -bottom-5 -right-5 w-28 h-28 border-b border-r border-gold/40 z-10" />
            <img
              src={ABOUT_IMAGE}
              alt="Atelier A&K Atelier"
              className="w-full h-[480px] object-cover grayscale-[15%]"
            />
          </div>
        </Reveal>

        <div>
          <Reveal>
            <Eyebrow>{t("about.badge")}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl text-foreground leading-tight">
              {title}
            </h2>
            <div className="mt-4 inline-flex items-center gap-2 text-gold">
              <span className="h-px w-6 bg-gold/60" />
              <span className="font-sans text-xs uppercase tracking-[0.2em]">{founded}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-muted-foreground font-sans leading-relaxed">{p1}</p>
            <p className="mt-4 text-muted-foreground font-sans leading-relaxed">{p2}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-12 grid grid-cols-2 gap-8 border-t border-border pt-10">
              {STATS.map((s) => (
                <div key={s.key} data-testid={`stat-${s.key}`}>
                  <div className="font-serif text-4xl md:text-5xl gold-gradient-text">
                    <Counter to={s.to} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.18em] text-muted-foreground font-sans">
                    {t(s.key)}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
